// keuangan.repo — data untuk menu Keuangan terpadu: aset, modal, persediaan, dan bahan
// indikator kesehatan. Angka pendapatan/HPP/beban tetap diambil dari agregat yang sudah ada
// (finance.view financeAgg) — jangan dihitung ulang di sini, satu sumber saja.
import { supabase } from '@core/supabase';
import { fetchAllPaged } from '@core/paging';
import type { Aset } from '@domain/finansial';
import { akunUntukCara } from '@domain/akunkas';

export interface AsetRow extends Aset {
  id: number; kategori: string | null; outlet: string | null; catatan: string | null;
  dijualHarga: number | null; byUser: string | null;
}
export type AsetInput = Omit<AsetRow, 'id' | 'byUser'>;

const KOL = 'id,nama,kategori,harga,tgl_beli,umur_bulan,nilai_sisa,outlet,catatan,dijual_tgl,dijual_harga,by_user,metode_susut,kelompok';
const keAset = (r: Record<string, unknown>): AsetRow => ({
  id: r['id'] as number, nama: r['nama'] as string, kategori: (r['kategori'] as string) ?? null,
  harga: r['harga'] as number, tglBeli: r['tgl_beli'] as string, umurBulan: r['umur_bulan'] as number,
  nilaiSisa: (r['nilai_sisa'] as number) ?? 0, outlet: (r['outlet'] as string) ?? null,
  catatan: (r['catatan'] as string) ?? null, dijualTgl: (r['dijual_tgl'] as string) ?? null,
  dijualHarga: (r['dijual_harga'] as number) ?? null, byUser: (r['by_user'] as string) ?? null,
  metodeSusut: (r['metode_susut'] as 'garis_lurus' | 'saldo_menurun') ?? null, kelompok: (r['kelompok'] as string) ?? null,
});

export async function listAset(): Promise<AsetRow[]> {
  const { data, error } = await supabase().from('assets').select(KOL).order('tgl_beli', { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []).map((r) => keAset(r as Record<string, unknown>));
}

export async function simpanAset(id: number | null, d: AsetInput & { byUser?: string }): Promise<void> {
  const row = {
    nama: d.nama, kategori: d.kategori, harga: d.harga, tgl_beli: d.tglBeli,
    umur_bulan: d.umurBulan, nilai_sisa: d.nilaiSisa ?? 0, outlet: d.outlet,
    catatan: d.catatan, dijual_tgl: d.dijualTgl ?? null, dijual_harga: d.dijualHarga,
    metode_susut: d.metodeSusut ?? 'garis_lurus', kelompok: d.kelompok ?? null,
    ...(id ? {} : { by_user: d.byUser ?? '' }),
  };
  const q = supabase().from('assets');
  const { error } = id ? await q.update(row).eq('id', id) : await q.insert(row);
  if (error) throw new Error(error.message);
}

export async function hapusAset(id: number): Promise<void> {
  const { error } = await supabase().from('assets').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

export interface EkuitasRow { id: number; tgl: string; jenis: 'setoran' | 'prive'; jumlah: number; outlet: string | null; catatan: string | null; by_user: string | null }

export async function listEkuitas(): Promise<EkuitasRow[]> {
  const { data, error } = await supabase().from('equity_moves')
    .select('id,tgl,jenis,jumlah,outlet,catatan,by_user').order('tgl', { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []) as EkuitasRow[];
}

export async function tambahEkuitas(d: { tgl: string; jenis: 'setoran' | 'prive'; jumlah: number; outlet: string | null; catatan: string; by_user: string }): Promise<void> {
  const { error } = await supabase().from('equity_moves').insert(d);
  if (error) throw new Error(error.message);
}

export async function hapusEkuitas(id: number): Promise<void> {
  const { error } = await supabase().from('equity_moves').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

/**
 * Nilai persediaan sparepart = Σ stok × harga. Aset lancar yang sering terlupakan.
 *
 * "Mati" = punya stok tapi TIDAK PERNAH terpakai dalam `hariMati` terakhir. Diukur dari
 * pemakaian nyata (penyerahan sparepart ke tiket), bukan dari tanggal barangnya dibuat —
 * barang yang dicatat setahun lalu tapi laku tiap minggu jelas bukan barang mati.
 */
export async function nilaiPersediaan(hariMati = 90): Promise<{ nilai: number; matiJml: number; matiNilai: number }> {
  const batasISO = new Date(Date.now() - hariMati * 86400000).toISOString();
  // Katalog sparepart bisa puluhan ribu SKU (impor Isx/Odoo) → WAJIB paging, kalau tidak nilai persediaan salah.
  const [rows, req] = await Promise.all([
    fetchAllPaged<{ barcode: string | null; nama: string; stok: number | null; harga: number | null }>((a, b) =>
      supabase().from('spareparts').select('id,barcode,nama,stok,harga').order('id').range(a, b)),
    supabase().from('sparepart_requests').select('serah,created_at').not('serah', 'is', null).gte('created_at', batasISO),
  ]);

  // Kunci pemakaian: barcode bila ada, kalau tidak nama (sama seperti pencocokan di modul sparepart).
  const terpakai = new Set<string>();
  for (const r of ((req.data ?? []) as { serah: { items?: { barcode?: string; nama?: string }[] } | null }[])) {
    for (const it of r.serah?.items ?? []) terpakai.add((it.barcode || it.nama || '').toLowerCase());
  }

  let nilai = 0, matiJml = 0, matiNilai = 0;
  for (const r of rows) {
    const v = Math.max(0, r.stok ?? 0) * Math.max(0, r.harga ?? 0);
    nilai += v;
    const kunci = (r.barcode || r.nama || '').toLowerCase();
    if (v > 0 && !terpakai.has(kunci)) { matiJml++; matiNilai += v; }
  }
  return { nilai, matiJml, matiNilai };
}

/** Pendapatan per pelanggan pada periode → untuk indikator konsentrasi. */
export async function pendapatanPerPelanggan(dari: string, sampai: string): Promise<number[]> {
  const data = await fetchAllPaged<{ customer_id: number | null; biaya: number | null; verified: boolean | null }>((a, b) =>
    supabase().from('tickets').select('customer_id,biaya,dp,verified,lunas,refund,arsip_keu,tgl_ambil_iso')
      .eq('status', 'Sudah Diambil').eq('arsip_keu', false)
      .gte('tgl_ambil_iso', dari).lte('tgl_ambil_iso', sampai).order('no').range(a, b));
  const m = new Map<number, number>();
  for (const r of data) {
    if (!r.customer_id || !r.verified) continue;
    m.set(r.customer_id, (m.get(r.customer_id) ?? 0) + (r.biaya ?? 0));
  }
  return [...m.values()];
}

/** Nilai servis ulang & klaim garansi pada periode → indikator biaya kualitas. */
export async function nilaiServisUlang(dari: string, sampai: string): Promise<number> {
  const data = await fetchAllPaged<{ data: { modal?: number } | null }>((d, s) => supabase().from('ticket_rounds')
    .select('data,created_at')
    .gte('created_at', `${dari}T00:00:00Z`).lte('created_at', `${sampai}T23:59:59Z`)
    .order('created_at').range(d, s));
  // Nilai sesi lama yang harus diulang = modal yang sudah dikeluarkan pada sesi itu.
  return data.reduce((a, r) => a + (r.data?.modal ?? 0), 0);
}

export interface SaldoAwal { kasAwal: number; modalAwal: number; tglAwal: string }
export const DEFAULT_SALDO_AWAL: SaldoAwal = { kasAwal: 0, modalAwal: 0, tglAwal: '' };

/** Saldo awal PER-ENTITAS (kode cabang | 'PUSAT'). */
export type SaldoAwalMap = Record<string, SaldoAwal>;
/** Normalisasi setting `saldo_awal`: bentuk lama (flat {kasAwal,…}) → dipetakan ke PUSAT (kompat mundur). */
export function normalizeSaldoAwal(raw: unknown): SaldoAwalMap {
  if (!raw || typeof raw !== 'object') return {};
  const o = raw as Record<string, unknown>;
  if ('kasAwal' in o || 'modalAwal' in o || 'tglAwal' in o) return { PUSAT: { ...DEFAULT_SALDO_AWAL, ...(o as Partial<SaldoAwal>) } };
  const out: SaldoAwalMap = {};
  for (const [k, v] of Object.entries(o)) if (v && typeof v === 'object') out[k] = { ...DEFAULT_SALDO_AWAL, ...(v as Partial<SaldoAwal>) };
  return out;
}


// ── Rincian kas per akun (untuk Posisi Keuangan) ──────────────────────────────
export interface SaldoAkun { id: number; nama: string; jenis: string; outlet: string | null; saldo: number; kode: string | null }

/**
 * Saldo tiap akun kas SEMUA WAKTU = saldo awal akun + mutasi non-tiket + kas dari tiket.
 *
 * Kas tiket dihitung dari tiketnya (bukan dari salinan di buku kas) supaya tak ada uang yang
 * terhitung dua kali. Yang tak bisa dipetakan ke akun mana pun — nota tanpa cara bayar —
 * sengaja TIDAK dibagi rata; ia muncul sebagai "Belum ditempel ke akun" di layar, karena
 * menyembunyikannya sama saja mengaku tahu sesuatu yang tidak diketahui.
 */
export async function saldoAkunSemua(): Promise<SaldoAkun[]> {
  const sb = supabase();
  // cash_moves & tickets DIPAGING — >1.000 baris akan terpotong diam-diam → saldo akun & Kas salah.
  const [akunR, mutasi, tiket] = await Promise.all([
    sb.from('cash_accounts').select('id,nama,jenis,outlet,saldo_awal,kas_kecil,aktif,urut,kode_akun').eq('aktif', true).order('urut'),
    fetchAllPaged<{ akun_id: number; arah: string; jumlah: number }>((a, b) =>
      sb.from('cash_moves').select('akun_id,arah,jumlah').order('id').range(a, b)),
    fetchAllPaged<Record<string, unknown>>((a, b) =>
      sb.from('tickets').select('outlet,dp,dp_via,biaya,biaya_simpan,lunas,verified,cara_bayar,lunas_via,refund')
        .eq('arsip_keu', false).order('no').range(a, b)),
  ]);
  if (akunR.error) throw new Error(akunR.error.message);
  const akun = (akunR.data ?? []) as { id: number; nama: string; jenis: string; outlet: string | null; saldo_awal: number; kode_akun: string | null }[];
  const hasil = new Map<number, number>(akun.map((a) => [a.id, a.saldo_awal ?? 0]));

  for (const m of mutasi) {
    hasil.set(m.akun_id, (hasil.get(m.akun_id) ?? 0) + (m.arah === 'masuk' ? m.jumlah : -m.jumlah));
  }

  const ringkas = akun.map((a) => ({ id: a.id, nama: a.nama, jenis: a.jenis as 'tunai', outlet: a.outlet,
    saldoAwal: 0, tglAwal: null, aktif: true, urut: 0 }));
  const tempel = (cara: string | null, outlet: string | null, nilai: number): void => {
    if (!nilai) return;
    const a = akunUntukCara(cara, ringkas, outlet);
    if (a) hasil.set(a.id, (hasil.get(a.id) ?? 0) + nilai);
  };
  for (const t of tiket) {
    const outlet = (t['outlet'] as string) ?? null;
    tempel((t['dp_via'] as string) ?? null, outlet, (t['dp'] as number) ?? 0);
    if (t['lunas']) {
      const total = ((t['biaya'] as number) ?? 0) + ((t['biaya_simpan'] as number) ?? 0);
      const sisa = Math.max(0, total - ((t['dp'] as number) ?? 0));
      tempel(((t['lunas_via'] as string) || (t['cara_bayar'] as string)) ?? null, outlet, sisa);
    }
    const rf = t['refund'] as { amount?: number; status?: string; method?: string } | null;
    if (rf?.status === 'verified' && rf.amount) tempel(rf.method ?? null, outlet, -rf.amount);
  }
  return akun.map((a) => ({ id: a.id, nama: a.nama, jenis: a.jenis, outlet: a.outlet, saldo: Math.round(hasil.get(a.id) ?? 0), kode: a.kode_akun ?? null }));
}
